import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@utils";

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  description = null,
  titleClassName,
  descriptionClassName,
  bodyClassName,
  fullHeight = false,
  maxHeight,
  preventBackdropClose = false,
  allowOverflow = false,
  elevated = false,
  // Sits below a normal drawer (z-40) but above the app chrome (sidebar z-30), so a drawer opened
  // from this modal — and any modal opened from that drawer — stack on top in the natural order.
  lowered = false,
}) {
  return (
    <Dialog
      open={isOpen}
      onClose={preventBackdropClose ? () => {} : onClose}
      className={cn("relative", elevated ? "z-[60]" : lowered ? "z-[35]" : "z-50")}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen">
        <div className="flex h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            style={maxHeight ? { maxHeight } : undefined}
            className={cn(
              "min-h-0 relative transform flex flex-col rounded-xl bg-white max-h-[80%] text-left shadow-xl transition-all",
              allowOverflow ? "overflow-visible" : "overflow-hidden",
              fullHeight && "h-[80%]",
              "data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200",
              "data-[enter]:ease-out data-[leave]:ease-in sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95",
              "transition-[width,max-width] duration-300 ease-in-out",
              {
                "w-[480px] max-w-[480px]": size === "sm",
                "w-[720px] max-w-[720px]": size === "md",
                "w-[840px] max-w-[840px]": size === "md-lg",
                "w-[960px] max-w-[960px]": size === "lg",
                "w-[1200px] max-w-[1200px]": size === "xl",
              },
            )}
          >
            <div className="flex shrink-0 items-center border-b border-zinc-200 p-5 h-100 max-w-100 overflow-auto">
              <DialogTitle as="h3" className="flex flex-col gap-1 flex-1 min-w-0">
                <span
                  className={cn(
                    "text-base font-semibold text-zinc-900",
                    titleClassName,
                  )}
                >
                  {title}
                </span>
                {description && (
                  <span className={cn("text-sm text-zinc-600", descriptionClassName)}>
                    {description}
                  </span>
                )}
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="ml-auto rounded-md bg-white text-zinc-400 hover:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div
              className={cn(
                "flex-1 min-h-0",
                bodyClassName ||
                  (allowOverflow ? "overflow-visible" : "overflow-y-auto"),
              )}
            >
              {children}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
